import { Schedule as ScheduleIcon } from '@mui/icons-material';
import { Button, DialogActions, DialogContent, Tooltip, Typography } from '@mui/material';
import { amber, green } from '@mui/material/colors';
import * as Schema from 'generated/graphql/schema';
import { FunctionComponent } from 'react';
import { useTranslation } from 'react-i18next';
import { makeStyles } from 'tss-react/mui';

import TextContentArea from '@/components/containers/text-content-area';
import AndonCallDialogHeader from '@/components/dialogs/andon/header';
import { buildSummary } from '@/helpers/andon';
import { useUtils } from '@/hocs';

const useStyles = makeStyles()((theme) => ({
  iconSizeLeft: {
    color: 'inherit',
    height: '1em !important',
    width: '1em !important',
    fontSize: theme.typography.pxToRem(24),
    marginRight: theme.spacing(),
  },
  iconSizeRight: {
    color: 'inherit',
    height: '1em !important',
    width: '1em !important',
    fontSize: theme.typography.pxToRem(24),
    marginLeft: theme.spacing(),
  },
  buttonWithIconRight: {
    '& > span > svg': {
      marginLeft: theme.spacing(),
    },
  },
  buttonWithIconLeft: {
    '& > span > svg': {
      marginRight: theme.spacing(),
    },
  },
  hideWhileSmall: {
    [theme.breakpoints.down('md')]: {
      display: 'none',
    },
  },
  buttonNegative: {
    backgroundColor: theme.palette.error.main,
    color: theme.palette.getContrastText(theme.palette.error.main),
    '&:hover': {
      backgroundColor: theme.palette.error.main,
    },
  },
  buttonNeutral: {
    backgroundColor: amber[600],
    color: theme.palette.getContrastText(amber[600]),
    '&:hover': {
      backgroundColor: amber[600],
    },
  },
  buttonPositive: {
    backgroundColor: green[600],
    color: theme.palette.getContrastText(green[600]),
    '&:hover': {
      backgroundColor: green[600],
    },
  },
}));

export interface Properties {
  call: Schema.AndonListCallsQuery['andon'][0]['calls'][0];
  onClose(..._: unknown[]): void;
}

const AndonResolveCallDialog: FunctionComponent<Properties> = ({ call, onClose }) => {
  const [t] = useTranslation();
  const { classes } = useStyles();
  const utils = useUtils();

  const callAction = call.action.pending;
  if (!callAction) {
    return null;
  }

  const summary = buildSummary(call.action, utils);

  return (
    <AndonCallDialogHeader onClose={onClose}>
      <DialogContent>
        {callAction.author ? (
          <TextContentArea>
            <Typography variant="subtitle2">{t(['andon:call:takenBy'], { defaultValue: 'Taken by' })}</Typography>
            <Typography>{callAction.author}</Typography>
          </TextContentArea>
        ) : null}
        <TextContentArea>
          <Typography variant="subtitle2">{t(['shared:summary'], { defaultValue: 'Summary' })}</Typography>
          <Typography>{summary}</Typography>
        </TextContentArea>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} color="primary" className={classes.hideWhileSmall}>
          {t(['shared:close'], { defaultValue: 'Close' })}
        </Button>
        <Tooltip
          placement={'top'}
          title={t(['andon:workOrder:pending'], {
            defaultValue: "A work order has been initiated for this call and it's in a pending state",
          })}
        >
          <span>
            <Button disabled color="primary" variant="contained" className={classes.buttonWithIconLeft}>
              {<ScheduleIcon />}
              {t(['shared:pending'], { defaultValue: 'Pending...' })}
            </Button>
          </span>
        </Tooltip>
      </DialogActions>
    </AndonCallDialogHeader>
  );
};

export default AndonResolveCallDialog;
